import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, catchError, map, of, throwError } from 'rxjs';
import { AuthInterface, AuthorisationInterface, PermisosInterface, UserInterface } from '../interfaces/auth';
import { AuthStatus } from '../interfaces/auth-status.enum';
import { ManejoErroresService } from '../../services/manejo-errores/manejo-errores.service';
import { ResponseGenericInterface } from '../../interface/response-generic.interface';
import { environment } from '../../../environments/environment';

@Injectable({
    providedIn: 'root'
})
export class AuthService {

    private readonly URL_SERVE_API = `${environment.url_serve_api_auth}`; // URL to web api

    // Claves de localStorage con prefijo del portal para evitar conflictos
    // entre portales distintos corriendo en el mismo origen (ej. localhost:4200)
    private readonly STORAGE_KEY_TOKEN  = `redi_${environment.portal}_token`;
    private readonly STORAGE_KEY_USER   = `redi_${environment.portal}_user`;
    private readonly STORAGE_KEY_CODES  = `redi_${environment.portal}_codes`;
    public  readonly STORAGE_KEY_URL    = `redi_${environment.portal}_url`;

    private http = inject(HttpClient);

    private _currentUser = signal<UserInterface | null>(null);
    private _authStatus = signal<AuthStatus>(AuthStatus.checking);
    private _currentUserPermisos = signal<PermisosInterface | null>(null);

    //! Al mundo exterior
    public currentUser = computed(() => this._currentUser());
    public authStatus = computed(() => this._authStatus());
    public currentUserPermisos = computed(() => this._currentUserPermisos());

    public isAuthenticated = computed(
        () => this._authStatus() === AuthStatus.authenticated
    );

    public visible_login: boolean = false;


    constructor(private manejoErrores: ManejoErroresService,) {
        //TODO implementar checkAuthStatus cuando este desarrollado el modulo de autenticacion
        this.checkAuthStatus().subscribe();

    }


    login(username: string, password: string): Observable<boolean> {

        const url = `${this.URL_SERVE_API}authR/login`;
        let portal: number = environment.config_id_portal;
        const payload = { username, password, portal };

        return this.http.post<ResponseGenericInterface<AuthInterface>>(url, payload)
            .pipe(
                map((response) => this.setAuthentication(response.data, response.data.authorisation)),
                catchError((err: HttpErrorResponse) => {
                    return this.manejoErrores.handleErrors(err)
                })
            );
    }



    private setAuthentication(data: AuthInterface, token: AuthorisationInterface): boolean {
        this._currentUser.set(data.user);
        this._authStatus.set(AuthStatus.authenticated);

        if (data.extra) {
            this._currentUserPermisos.set(data.extra.permisos);
            localStorage.setItem(this.STORAGE_KEY_CODES, JSON.stringify(data.extra.permisos));
        } else {
            let codes = localStorage.getItem(this.STORAGE_KEY_CODES);
            if (codes) {
                let auxPermisos: PermisosInterface = JSON.parse(codes) as PermisosInterface
                this._currentUserPermisos.set(auxPermisos);
                //console.debug(auxPermisos);
            }
        }

        // Guardar datos del usuario en localStorage con clave específica del portal
        localStorage.setItem(this.STORAGE_KEY_USER, JSON.stringify(data.user));
        localStorage.setItem(this.STORAGE_KEY_TOKEN, token.token);
        console.debug("Login", this.currentUser(), this.authStatus(), this.currentUserPermisos());
        return true
    }


    /**
     * Restaura la sesión desde localStorage de forma optimista.
     * Esto permite que la UI muestre el estado autenticado inmediatamente
     * sin esperar la respuesta del servidor.
     * @returns true si se pudo restaurar la sesión, false si no hay datos suficientes.
     */
    private restoreSessionFromStorage(): boolean {
        const token = localStorage.getItem(this.STORAGE_KEY_TOKEN);
        const userStr = localStorage.getItem(this.STORAGE_KEY_USER);
        const codesStr = localStorage.getItem(this.STORAGE_KEY_CODES);

        if (!token) {
            return false;
        }

        // Restaurar usuario desde localStorage
        if (userStr) {
            try {
                const user: UserInterface = JSON.parse(userStr);
                this._currentUser.set(user);
            } catch (e) {
                console.warn('Error al restaurar usuario desde localStorage:', e);
            }
        }

        // Restaurar permisos desde localStorage
        if (codesStr) {
            try {
                const permisos: PermisosInterface = JSON.parse(codesStr);
                this._currentUserPermisos.set(permisos);
            } catch (e) {
                console.warn('Error al restaurar permisos desde localStorage:', e);
            }
        }

        // Marcar como autenticado inmediatamente
        this._authStatus.set(AuthStatus.authenticated);
        console.debug(`Sesión restaurada desde localStorage [portal: ${environment.portal}]`);
        return true;
    }


    /**
     * Decodifica el payload de un token JWT y verifica si ya expiró.
     * Un JWT tiene el formato: header.payload.signature
     * El payload contiene el campo 'exp' con la fecha de expiración en segundos Unix.
     * @param token El token JWT a verificar
     * @returns true si el token ya expiró o no se puede decodificar, false si sigue vigente
     */
    private isTokenExpired(token: string): boolean {
        try {
            // El payload es la segunda parte del JWT (separado por puntos)
            const payload = token.split('.')[1];
            if (!payload) {
                return true;
            }

            // Decodificar base64 → JSON
            const decoded = JSON.parse(atob(payload));

            // Verificar campo 'exp' (fecha de expiración en segundos Unix)
            if (!decoded.exp) {
                return true; // Sin expiración definida, se trata como expirado
            }

            // Comparar con la fecha actual (en segundos)
            const ahora = Math.floor(Date.now() / 1000);
            return decoded.exp < ahora;
        } catch (e) {
            console.warn('Error al decodificar token JWT:', e);
            return true; // Si no se puede decodificar, se trata como expirado
        }
    }


    checkAuthStatus(): Observable<boolean> {

        const token = localStorage.getItem(this.STORAGE_KEY_TOKEN);

        if (!token) {
            this._authStatus.set(AuthStatus.notAuthenticated);
            return of(false);
        }

        // Verificar si el token JWT ya expiró
        if (this.isTokenExpired(token)) {
            console.debug('Token JWT expirado, cerrando sesión automáticamente.');
            this.logout();
            return of(false);
        }

        // Token vigente: restaurar sesión desde localStorage
        this.restoreSessionFromStorage();
        return of(true);

    }

    logout() {

        localStorage.removeItem(this.STORAGE_KEY_TOKEN);
        localStorage.removeItem(this.STORAGE_KEY_CODES);
        localStorage.removeItem(this.STORAGE_KEY_URL);
        localStorage.removeItem(this.STORAGE_KEY_USER);

        this._currentUser.set(null);
        this._currentUserPermisos.set(null);
        this._authStatus.set(AuthStatus.notAuthenticated);
        console.debug("Logout", this.currentUser(), this.authStatus(), this.currentUserPermisos());

    }


    onUserAdmin(): boolean {
        let userPermisos = computed(() => this.currentUserPermisos());
        let auxPermiso: boolean | undefined = false;
        auxPermiso = userPermisos()?.codes.includes(environment.codeAdmin);
        //console.debug("Permisos", userPermisos()?.codes.includes(environment.codeAdmin))
        return auxPermiso !== undefined && environment.offline === false ? auxPermiso : false;
    }

    onUserNotInvitado(): boolean {
        let userPermisos = computed(() => this.currentUserPermisos());
        let auxPermiso: boolean | undefined = false;
        auxPermiso = userPermisos()?.codes.includes('N12');
        //console.debug("Permisos", userPermisos()?.codes.includes(environment.codeAdmin))
        return auxPermiso !== undefined && environment.offline === false ? auxPermiso : false;
    }

    onUserGrado(codeGrado: string): boolean {
        if (environment.offline) {
            return true

        }
        let userPermisos = computed(() => this.currentUserPermisos());
        let auxPermiso: boolean | undefined = false;
        auxPermiso = userPermisos()?.codes.includes(codeGrado);
        // console.debug("Permisos",userPermisos()?.codes,codeGrado, userPermisos()?.codes.includes(codeGrado))
        return auxPermiso !== undefined && environment.offline === false ? auxPermiso : false;
    }



    getGradosUser(): number[] {

        let userPermisos = computed(() => this.currentUserPermisos());
        let listGradosCode: string[] = [
            "N02",
            "N03",
            "N04",
            "N05",
            "N06",
            "N07"
        ]
        let listIDgrados: number[] = [];

        listGradosCode.map((code) => {

            if (userPermisos()?.codes.includes(code)) {
                switch (code) {
                    case "N02"://1
                        listIDgrados.push(5);
                        break;
                    case "N03"://2
                        listIDgrados.push(6);
                        break;
                    case "N04"://3
                        listIDgrados.push(7);
                        break;
                    case "N05"://4
                        listIDgrados.push(8);
                        break;
                    case "N06"://5
                        listIDgrados.push(9);
                        break;
                    case "N07"://6
                        listIDgrados.push(10);
                        break;
                }

            }
        })



        return listIDgrados
    }

    loginOffline() {

        let auxData: AuthInterface = {
            user: {
                id: 0,
                email: '',
                username: 'PortalOffline',
                name: 'PortalOffline',
                created_at: new Date(),
                updated_at: new Date(),
                email_verified_at: null
            },
            authorisation: {
                token: '',
                type: ''
            },
            extra: {
                permisos: {
                    codes: [
                    ]
                }
            }
        }


        this.setAuthentication(auxData, {
            token: '',
            type: ''
        });
        //this._currentUser.set(auxUser);
        // this._authStatus.set(AuthStatus.authenticated);


    }



}
